import { Box, VStack } from "@chakra-ui/react";
import type { UIMessage } from "ai";
import {
  memo,
  type Dispatch,
  type MutableRefObject,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useRef,
  useState,
} from "react";

import { AgentAssistantMessageBubble } from "./agent-thread-messages";
import { AgentAssistantThumbsFeedback } from "./agent-assistant-thumbs-feedback";
import { AgentAssistantWrittenFeedbackInline } from "./agent-assistant-written-feedback-inline";
import {
  advisorMessageFeedbackComment,
  advisorMessageFeedbackRating,
} from "./advisor-v2-chat-history";
import { AgentToolProgress } from "./agent-tool-progress";
import {
  type DataSuggestionOption,
  getDataSuggestionsOptions,
  shouldShowAdvisorFollowUpChips,
} from "./data-suggestions";
import { getSupportHandoffData, hasSupportHandoff } from "./data-support-handoff";
import { AdvisorFollowUpSuggestions } from "./suggestion-chip";
import { SupportEmailCard } from "./support-email-card";
import type { AcceptSupportHandoffResult } from "./support-handoff-accept-api";
import { SupportHandoffOfferCard, type SupportHandoffOfferState } from "./support-handoff-offer-card";
import { useAdvisorWrittenFeedbackInlineState } from "./use-advisor-written-feedback-inline-state";
import {
  agentAssistantThumbsVisible,
  useAgentAssistantMessageFeedback,
  type AgentSubmitMessageFeedbackFn,
} from "./use-agent-assistant-message-feedback";
import { visibleToolProgressItems } from "./tool-progress-visibility";
import { getToolProgressItems } from "./tool-progress";

export type AgentAssistantSupportHandoffProps = {
  /**
   * Accepts a handoff offer server-side with the offer's id (``POST .../support-handoff``).
   * Must resolve to the route outcome rather than throw; the row renders the result.
   */
  onAcceptOffer: (offerId: string) => Promise<AcceptSupportHandoffResult>;
};

export type AgentAssistantMessageRowProps = {
  message: UIMessage;
  text: string;
  isLast: boolean;
  busy: boolean;
  canUseAgent: boolean;
  accentColor: string;
  onSendSuggestion: (option: { text: string; use_case?: string }) => void;
  submitMessageFeedback: AgentSubmitMessageFeedbackFn;
  setMessages: Dispatch<SetStateAction<UIMessage[]>>;
  writtenFeedbackMessageId: string | null;
  setWrittenFeedbackMessageId: Dispatch<SetStateAction<string | null>>;
  kinFeedbackSubmitPendingRef: MutableRefObject<boolean>;
  closeKinInlineFeedbackRef: MutableRefObject<(() => void) | null>;
  onFeedbackError: (error: unknown) => void;
  /**
   * Org Kin only. When set, every assistant message carrying a `ticket_offer` handoff part renders
   * {@link SupportHandoffOfferCard}, not just the last one: the offer is the persisted message and
   * stays acceptable after intervening turns (TDD 5.4). Pass a stable object (memo compares by reference).
   */
  supportHandoff?: AgentAssistantSupportHandoffProps;
  /** Same-turn chips from another assistant row (e.g. trailing shell); shown only when `isLast`. */
  followUpOptionsOverride?: DataSuggestionOption[];
};

function assistantFooterStateKey(
  message: UIMessage,
  _includeSupportHandoff: boolean,
  followUpOptionsOverride?: DataSuggestionOption[],
): string {
  return JSON.stringify({
    followUps:
      followUpOptionsOverride ?? getDataSuggestionsOptions(message) ?? [],
    // Always track handoff — it suppresses follow-up chips on all surfaces (partner + org).
    supportHandoff: hasSupportHandoff(message),
    feedbackRating: advisorMessageFeedbackRating(message),
    feedbackComment: advisorMessageFeedbackComment(message),
  });
}

function toolProgressStateKey(message: UIMessage): string {
  return getToolProgressItems(message)
    .map((item) => `${item.toolCallId}:${item.status}`)
    .join("|");
}

function AgentAssistantMessageRowInner({
  footerStateKey: _footerStateKey,
  message,
  text,
  isLast,
  busy,
  canUseAgent,
  accentColor,
  onSendSuggestion,
  submitMessageFeedback,
  setMessages,
  writtenFeedbackMessageId,
  setWrittenFeedbackMessageId,
  kinFeedbackSubmitPendingRef,
  closeKinInlineFeedbackRef,
  onFeedbackError,
  supportHandoff,
  followUpOptionsOverride,
}: AgentAssistantMessageRowMemoProps) {
  const rawProgress = getToolProgressItems(message);
  const visibleProgress = visibleToolProgressItems(rawProgress, {
    busy: isLast && busy,
  });
  const hasAssistantText = text.trim().length > 0;
  // Drop progress as soon as the answer starts so the bubble can replace it in-place (no stack jump).
  const progressNode =
    !hasAssistantText && visibleProgress.length ? (
      <AgentToolProgress items={visibleProgress} />
    ) : undefined;
  const showThinkingDots =
    isLast &&
    busy &&
    !hasAssistantText &&
    visibleProgress.length === 0 &&
    rawProgress.length > 0;
  const followOpts =
    followUpOptionsOverride ?? getDataSuggestionsOptions(message);
  const handoff = getSupportHandoffData(message);
  const offerId = handoff?.variant === 'ticket_offer' ? handoff.offerId : undefined;

  const [offerState, setOfferState] = useState<SupportHandoffOfferState>('idle');
  const [offerDeclined, setOfferDeclined] = useState(false);
  /** Set when the accept route answered 403 `support_handoff_not_entitled`: the FREE card replaces the offer. */
  const [notEntitledSupportEmail, setNotEntitledSupportEmail] = useState<string | null>(null);
  // Ref, not state: a second click that lands before React re-renders with `pending` must still
  // be dropped so one offer never produces two accept calls.
  const acceptInFlightRef = useRef(false);

  const onAcceptOffer = supportHandoff?.onAcceptOffer;
  const handleAcceptOffer = useCallback(async () => {
    if (!onAcceptOffer || !offerId || acceptInFlightRef.current) return;
    acceptInFlightRef.current = true;
    setOfferState('pending');
    try {
      const result = await onAcceptOffer(offerId);
      if (result.kind === 'accepted') {
        setOfferState('accepted');
      } else if (result.kind === 'not_entitled') {
        setNotEntitledSupportEmail(result.supportEmail);
        setOfferState('idle');
      } else {
        setOfferState('failed');
      }
    } finally {
      acceptInFlightRef.current = false;
    }
  }, [onAcceptOffer, offerId]);
  const handleDeclineOffer = useCallback(() => setOfferDeclined(true), []);

  const emailCardSupportEmail =
    notEntitledSupportEmail ?? (handoff?.variant === 'email_card' ? handoff.supportEmail : null);
  // The card waits for Kin's sentence. The handoff part arrives when the model calls
  // ``route_to_support``, a round trip before the support lane produces any text, so showing it
  // immediately puts the offer on screen above the words that explain it. ``!busy`` is the
  // fallback: a turn that ends with no text at all still shows its card rather than losing it.
  // Only the row being streamed waits: ``busy`` is thread-wide, and earlier rows' cards are done.
  const handoffReady = text.trim().length > 0 || !(isLast && busy);
  const showEmailCard = emailCardSupportEmail !== null && handoffReady;
  const showOfferCard =
    Boolean(onAcceptOffer && offerId && !showEmailCard && !offerDeclined) && handoffReady;
  const showFollowUps = shouldShowAdvisorFollowUpChips(message, followOpts, { isLast, busy });
  const showThumbs = agentAssistantThumbsVisible({ messageId: message.id, isLast, busy });
  const thumbDownIntentBridgeRef = useRef<() => void>(() => {});

  const { onRate, displayRating, confirmThumbDown } =
    useAgentAssistantMessageFeedback({
      message,
      submitMessageFeedback,
      setMessages,
      onError: onFeedbackError,
      thumbDownMode: "intent",
      onThumbDownIntent: () => {
        thumbDownIntentBridgeRef.current();
      },
    });

  const {
    writtenFeedbackOpen,
    onThumbDownIntent,
    inlineDraft,
    setInlineDraft,
    inlinePending,
    onInlineRequestClose,
    onInlineSkip,
    onInlineSubmit,
    submitOrSkipInFlight,
  } = useAdvisorWrittenFeedbackInlineState({
    messageId: message.id,
    writtenFeedbackMessageId,
    setWrittenFeedbackMessageId,
    kinFeedbackSubmitPendingRef,
    closeKinInlineFeedbackRef,
    confirmThumbDown,
  });
  thumbDownIntentBridgeRef.current = onThumbDownIntent;

  const assistantFooter: ReactNode =
    showThumbs || showEmailCard || showOfferCard || (showFollowUps && followOpts) ? (
      <>
        {showEmailCard ? (
          <Box w="100%">
            <SupportEmailCard supportEmail={emailCardSupportEmail} />
          </Box>
        ) : null}
        {showOfferCard ? (
          <Box w="100%">
            <SupportHandoffOfferCard
              state={offerState}
              onAccept={handleAcceptOffer}
              onDecline={handleDeclineOffer}
              disabled={!canUseAgent || busy}
            />
          </Box>
        ) : null}
        {showThumbs ? (
          <VStack align="stretch" gap={2} w="100%">
            <AgentAssistantThumbsFeedback
              rating={displayRating}
              disabled={
                !canUseAgent || writtenFeedbackOpen || submitOrSkipInFlight
              }
              accentColor={accentColor}
              onRate={onRate}
            />
            {writtenFeedbackOpen ? (
              <AgentAssistantWrittenFeedbackInline
                draft={inlineDraft}
                onDraftChange={setInlineDraft}
                disabled={!canUseAgent}
                pending={inlinePending}
                onRequestClose={onInlineRequestClose}
                onSkip={onInlineSkip}
                onSubmit={onInlineSubmit}
              />
            ) : null}
          </VStack>
        ) : null}
        {showFollowUps && followOpts ? (
          <Box w="100%">
            <AdvisorFollowUpSuggestions
              options={followOpts}
              onSelect={onSendSuggestion}
              disabled={!canUseAgent}
              hoverBorderColor={accentColor}
              hoverTextColor={accentColor}
            />
          </Box>
        ) : null}
      </>
    ) : undefined;

  return (
    <AgentAssistantMessageBubble
      text={text}
      progress={progressNode}
      footer={assistantFooter}
      thinkingDots={showThinkingDots}
    />
  );
}

export type AgentAssistantMessageRowMemoProps =
  AgentAssistantMessageRowProps & {
    /** Stable string for memo when `message` reference changes but footer-driving metadata is unchanged. */
    footerStateKey: string;
  };

export const AgentAssistantMessageRow = memo(
  AgentAssistantMessageRowInner,
  (a, b) => {
    return (
      a.isLast === b.isLast &&
      a.busy === b.busy &&
      a.canUseAgent === b.canUseAgent &&
      a.accentColor === b.accentColor &&
      a.onSendSuggestion === b.onSendSuggestion &&
      a.submitMessageFeedback === b.submitMessageFeedback &&
      a.setMessages === b.setMessages &&
      a.writtenFeedbackMessageId === b.writtenFeedbackMessageId &&
      a.setWrittenFeedbackMessageId === b.setWrittenFeedbackMessageId &&
      a.kinFeedbackSubmitPendingRef === b.kinFeedbackSubmitPendingRef &&
      a.closeKinInlineFeedbackRef === b.closeKinInlineFeedbackRef &&
      a.onFeedbackError === b.onFeedbackError &&
      a.supportHandoff === b.supportHandoff &&
      a.followUpOptionsOverride === b.followUpOptionsOverride &&
      a.message.id === b.message.id &&
      a.text === b.text &&
      a.footerStateKey === b.footerStateKey &&
      (!a.isLast ||
        !a.busy ||
        toolProgressStateKey(a.message) === toolProgressStateKey(b.message))
    );
  },
);

AgentAssistantMessageRow.displayName = "AgentAssistantMessageRow";

export { assistantFooterStateKey };
